import clsx from 'clsx';
import { CheckCircle2, Circle, CircleDot, TriangleAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { type ModelField, type WorkedStep, formatModelNumber, modelFieldError, parseModelValue } from './modelInputContract';

/**
 * Typed learner inputs with unit, range hint and inline error (CT-INTERACT C1 and C4). An invalid
 * value stays on screen with its message and the model keeps the last valid values. A value set
 * from outside (a preset) shows in the field. `sliderKey` adds a slider for the main variable.
 */
export function ModelInputPanel<K extends string>({
  legend,
  fields,
  values,
  onChange,
  sliderKey,
}: {
  legend: string;
  fields: readonly ModelField<K>[];
  values: Record<K, number>;
  onChange: (next: Record<K, number>) => void;
  sliderKey?: K;
}) {
  const [drafts, setDrafts] = useState<Partial<Record<K, string>>>({});
  const fieldFor = (field: ModelField<K>) => {
    const draft = drafts[field.key] ?? String(values[field.key]);
    const error = modelFieldError(field, parseModelValue(draft));
    if (error) return { value: draft, error };
    return { value: parseModelValue(draft) === values[field.key] ? draft : String(values[field.key]), error: null };
  };
  const update = (field: ModelField<K>, raw: string) => {
    setDrafts((current) => ({ ...current, [field.key]: raw }));
    const value = parseModelValue(raw);
    if (!modelFieldError(field, value)) onChange({ ...values, [field.key]: value });
  };
  const slider = sliderKey ? fields.find((field) => field.key === sliderKey) : undefined;

  return (
    <fieldset className="rounded-3xl border border-hairline bg-surface p-4">
      <legend className="px-1 text-sm font-extrabold text-ink">{legend}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((field) => {
          const { value, error } = fieldFor(field);
          return (
            <label key={field.key} className="block text-sm font-extrabold text-ink">
              {field.label}
              <span className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={value}
                  aria-invalid={error ? true : undefined}
                  onChange={(event) => update(field, event.target.value)}
                  className={clsx(
                    'h-11 w-full min-w-0 rounded-2xl border bg-canvas-pure px-3 text-base font-extrabold text-ink outline-none focus:border-brand-sky',
                    error ? 'border-brand-coral' : 'border-hairline',
                  )}
                />
                <span className="w-12 shrink-0 text-sm font-bold text-slate2">{field.unit}</span>
              </span>
              <span className={clsx('mt-1 block text-xs font-semibold', error ? 'text-brand-coral' : 'text-slate2')}>
                {error ?? `${field.min}–${field.max} ${field.unit}`}
              </span>
            </label>
          );
        })}
      </div>
      {slider ? (
        <label className="mt-4 block text-sm font-extrabold text-ink">
          <span className="flex items-center justify-between gap-2">
            <span>{slider.label}</span>
            <strong>
              {formatModelNumber(values[slider.key], 3)} {slider.unit}
            </strong>
          </span>
          <input
            aria-label={`${slider.label} slider`}
            type="range"
            min={slider.min}
            max={slider.max}
            step={slider.step}
            value={values[slider.key]}
            onChange={(event) => {
              const value = Number(event.target.value);
              setDrafts((current) => ({ ...current, [slider.key]: String(value) }));
              onChange({ ...values, [slider.key]: value });
            }}
            className="mt-2 h-11 w-full accent-brand-coral"
          />
        </label>
      ) : null}
    </fieldset>
  );
}

const STATUS_ICON = { done: CheckCircle2, current: CircleDot, 'not-reached': Circle } as const;

/**
 * The worked calculation (CT-INTERACT C2): an optional lead line, every step with its status, the
 * formula and the learner's numbers substituted, a result line and an optional model-limit note.
 */
export function WorkedStepsPanel({
  title,
  lead,
  leadTestId,
  steps,
  stepTestIdPrefix,
  result,
  resultTestId,
  limitNote,
}: {
  title: string;
  lead?: string;
  leadTestId?: string;
  steps: readonly WorkedStep[];
  stepTestIdPrefix: string;
  result: ReactNode;
  resultTestId?: string;
  limitNote?: string | null;
}) {
  return (
    <section aria-label={title} className="rounded-3xl border border-hairline bg-canvas-pure p-4">
      <h3 className="text-sm font-extrabold text-ink">{title}</h3>
      {lead ? (
        <p className="mt-2 rounded-2xl bg-wash-coral px-3 py-2 font-mono text-sm font-bold text-ink" data-testid={leadTestId}>
          {lead}
        </p>
      ) : null}
      <ol className="mt-3 grid gap-2">
        {steps.map((step, index) => {
          const Icon = STATUS_ICON[step.status];
          return (
            <li
              key={step.id}
              data-testid={`${stepTestIdPrefix}-${step.id}`}
              data-status={step.status}
              className={clsx(
                'rounded-2xl border px-3 py-2',
                step.status === 'current'
                  ? 'border-brand-sky bg-wash-sky'
                  : step.status === 'done'
                    ? 'border-hairline bg-surface'
                    : 'border-dashed border-hairline text-slate2 opacity-70',
              )}
            >
              <p className="flex flex-wrap items-center gap-x-2 text-sm font-extrabold text-ink">
                <Icon className={clsx('h-4 w-4 shrink-0', step.status === 'done' ? 'text-brand-mint' : 'text-brand-sky')} aria-hidden="true" />
                {index + 1}. {step.title}
                {step.note ? <span className="text-xs font-bold text-slate2">{step.note}</span> : null}
              </p>
              {step.status !== 'not-reached' ? (
                <p className="mt-1 font-mono text-xs font-semibold text-ink-soft">
                  <span className="text-slate2">{step.formula}: </span>
                  {step.working}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-sm font-extrabold text-ink" data-testid={resultTestId}>
        {result}
      </p>
      {limitNote ? (
        <p role="status" className="mt-2 flex items-start gap-2 rounded-2xl bg-wash-sunshine px-3 py-2 text-xs font-bold text-ink">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {limitNote}
        </p>
      ) : null}
    </section>
  );
}
